import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { format } from "date-fns";

import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  AddCircleOutlineOutlined,
  Close
} from "@mui/icons-material";

import apiService from "../../../service/Api";
import { RootState } from "../../../store/Index";
import { Stream } from "../../../types/streams";
import { PilotProps } from "../types/view-pilot";
import { RoleSkillTool } from "../types/add-crew-member";
import useDebounce from "../../../hooks/useDebounce";
import CustomTextField from "../../../components/common/Textfield";
import { AxiosError } from "axios";

interface IPilotAssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  pilot: PilotProps | undefined;
};

interface AssignmentResponseProps {
  crew_member: {
    full_name: string;
  };
};

export const PilotAssignmentDialog: React.FC<IPilotAssignmentDialogProps> = ({
  open,
  onClose,
  pilot
}) => {
  const [selectedStream, setSelectedStream] = useState<Stream | null>();
  const [selectedRole, setSelectedRole] = useState<RoleSkillTool | null>();

  const [assignmentLoading, setAssignmentLoading] = useState<boolean>(false);

  const [streamOptions, setStreamOptions] = useState<Stream[]>([]);
  const [streamVal, setStreamVal] = useState("");
  const [streamsLoading, setStreamsLoading] = useState(false);

  const [roleOptions, setRoleOptions] = useState<RoleSkillTool[]>([]);
  const [roleVal, setRoleVal] = useState("");
  const [rolesLoading, setRolesLoading] = useState(false);

  const debouncedStreamSearch = useDebounce(streamVal, 400);
  const debouncedRoleSearch = useDebounce(roleVal, 400);

  const reduxTheme = useSelector((state: RootState) => state.theme);

  const handleAssignment = async () => {
    setAssignmentLoading(true);
    if (!selectedStream) {
      toast.error('Please select a stream to assign this pilot');
    };

    if (!selectedRole) {
      toast.error('Please select a role to assign this pilot');
    };

    try {
      const payload = {
        stream_id: selectedStream?.id,
        crew_member_id: pilot?.id,
        assignment: [{
          role: selectedRole?.name,
          notes: ''
        }]
      };

      const assignment = await apiService.post<AssignmentResponseProps>(
        `/api/call-sheets/callsheet/`, payload);

      toast.success(
        `Successfully assigned ${assignment.data.crew_member.full_name} as ${selectedRole?.name} for the stream: ${selectedStream?.title}`
      );
      closeAndReset();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.message || "Failed to complete callsheet assignment. Please try again.");
      } else {
        console.error("Failed to complete callsheet assignment:", error);
      }
    } finally {
      setAssignmentLoading(false);
    }
  };

  const closeAndReset = () => {    
    setStreamVal("");
    setStreamOptions([]);
    onClose();
    setRoleVal("");
    setRoleOptions([]);
  };

  const fetchStreams = useCallback(async (searching: string) => {
    if (!searching) {
      setRoleOptions([]);
      return;
    }

    setStreamsLoading(true);
    try {
      const streams = await apiService.get<{ results: Stream[] }>(
        '/api/streams/stream/', { search: searching }
      );

      setStreamOptions(streams.data.results || []);
    } catch (err) {
      console.error("Failed to fetch streams:", err);
      setStreamOptions([]);
    } finally {
      setStreamsLoading(false);
    }
  }, []);

  const fetchRoles = useCallback(async (searching: string) => {
    if (!searching) {
      setRoleOptions([]);
      return;
    }

    setRolesLoading(true);
    try {
      const roles = await apiService.get<{ results: RoleSkillTool[] }>(
        '/api/crews/roles/', { search: searching }
      );

      setRoleOptions(roles.data.results || []);
    } catch (err) {
      console.error("Failed to fetch roles:", err);
      setRoleOptions([]);
    } finally {
      setRolesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStreams(debouncedStreamSearch);
  }, [debouncedStreamSearch, fetchStreams]);

  useEffect(() => {
    fetchRoles(debouncedRoleSearch);
  }, [debouncedRoleSearch, fetchRoles]);

  return (
    <Dialog
      open={open}
      onClose={closeAndReset}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Callsheet Assignment for {`${pilot?.name} ${pilot?.surname}`}
        <IconButton
          onClick={closeAndReset}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Autocomplete
            multiple={false}
            options={streamOptions}
            getOptionLabel={(option) => {
              const stream = option?.id ? `${option.id} - ${option.title}` : "No Streams To Display";
              return stream;
            }}
            value={selectedStream}
            onChange={(_, newValue) => setSelectedStream(newValue)}
            inputValue={streamVal}
            onInputChange={(_, newInput) => setStreamVal(newInput)}
            loading={streamsLoading}
            renderInput={(params) => (
              <CustomTextField
                {...params}
                placeholder="Search streams..."
                variant="outlined"
                helperText={streamVal.trim().length < 3 ? 'Type 3 letters to search' : ''}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {streamsLoading ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const stream = option?.id ? `${option.id} - ${option.title}` : "No Streams To Display";
                return (
                  <Chip
                    label={stream}
                    {...getTagProps({ index })}
                    key={option.id}
                    sx={{
                      backgroundColor: reduxTheme.primary_colors[800],
                      borderRadius: 1,
                      color: reduxTheme.primary_colors[100],
                    }}
                  />
                );
              })
            }
            sx={{ minWidth: 300 }}
          />
          <List
            sx={{
              backgroundColor: reduxTheme.primary_colors[800],
              borderRadius: 2,
              mt: 1
            }}
          >
            {streamVal.trim().length < 2 ? (
              <ListItem>
                <ListItemText
                  primary="Type at least 2 characters to search..."
                  sx={{ color: reduxTheme.primary_colors[100] }}
                />
              </ListItem>
            ) : streamsLoading ? (
              <ListItem>
                <CircularProgress
                  size={20}
                  sx={{ mr: 2, color: reduxTheme.primary_colors[100] }}
                />
                <ListItemText
                  primary="Searching..."
                  sx={{ color: reduxTheme.primary_colors[100] }}
                />
              </ListItem>
            ) : !selectedStream ? (
              <ListItem>
                <ListItemText
                  primary="No stream selected."
                  sx={{ color: reduxTheme.primary_colors[100] }}
                />
              </ListItem>
            ) : (
              <ListItem component="div">
                <Box>
                  <Typography variant={'subtitle2'}>{`${selectedStream?.title} - ${format(selectedStream?.date, 'dd/MM/yyyy')}`}</Typography>
                  <Typography variant={'caption'}>{`ID: ${selectedStream?.id}`}</Typography>
                </Box>
              </ListItem>
            )}
          </List>
        </Box>

        {selectedStream ? (
          <Box sx={{ mt: 1 }}>
            <Autocomplete
              multiple={false}
              options={roleOptions}
              getOptionLabel={(option) => {
                const role = option?.id ? `${option.id} - ${option.name}` : "No Roles To Display";
                return role;
              }}
              value={selectedRole}
              onChange={(_, newValue) => setSelectedRole(newValue)}
              inputValue={roleVal}
              onInputChange={(_, newInput) => setRoleVal(newInput)}
              loading={rolesLoading}
              renderInput={(params) => (
                <CustomTextField
                  {...params}
                  placeholder="Search roles..."
                  variant="outlined"
                  helperText={roleVal.trim().length < 3 ? 'Type 3 letters to search' : ''}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {rolesLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const role = option?.id ? `${option.id} - ${option.name}` : "No Roles To Display";
                  return (
                    <Chip
                      label={role}
                      {...getTagProps({ index })}
                      key={option.id}
                      sx={{
                        backgroundColor: reduxTheme.primary_colors[800],
                        borderRadius: 1,
                        color: reduxTheme.primary_colors[100],
                      }}
                    />
                  );
                })
              }
              sx={{ minWidth: 300 }}
            />
            <List
              sx={{
                backgroundColor: reduxTheme.primary_colors[800],
                borderRadius: 2,
                mt: 1
              }}
            >
              {roleVal.trim().length < 2 ? (
                <ListItem>
                  <ListItemText
                    primary="Type at least 2 characters to search..."
                    sx={{ color: reduxTheme.primary_colors[100] }}
                  />
                </ListItem>
              ) : rolesLoading ? (
                <ListItem>
                  <CircularProgress
                    size={20}
                    sx={{ mr: 2, color: reduxTheme.primary_colors[100] }}
                  />
                  <ListItemText
                    primary="Searching..."
                    sx={{ color: reduxTheme.primary_colors[100] }}
                  />
                </ListItem>
              ) : !selectedRole ? (
                <ListItem>
                  <ListItemText
                    primary="No role selected."
                    sx={{ color: reduxTheme.primary_colors[100] }}
                  />
                </ListItem>
              ) : (
                <ListItem component="div">
                  <Typography variant={'subtitle2'}>{`${selectedRole?.name}`}</Typography>
                </ListItem>
              )}
            </List>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={closeAndReset}
          sx={{
            color: reduxTheme.primary_colors[100],
            backgroundColor: reduxTheme.primary_colors[800],
          }}
        >
          Discard
        </Button>
        <Button
          startIcon={assignmentLoading ? (
            <CircularProgress size={12} sx={{ color: '#FFF' }} />
          ) : (
            <AddCircleOutlineOutlined />
          )}
          sx={{
            color: reduxTheme.primary_colors[900],
            backgroundColor: reduxTheme.primary_colors[400],
          }}
          disabled={assignmentLoading}
          onClick={() => handleAssignment()}
        >
          {`Assign This Pilot`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PilotAssignmentDialog;
